'use client'

import React, { useState } from 'react'

import { Menu, X } from 'lucide-react'
import { usePathname } from 'next/navigation'

import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { CMSLink } from '@/components/Link'
import { Button } from '@/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import type { Locale } from '@/i18n'
import type { Header as HeaderType } from '@/payload-types'
import { cn } from '@/utilities/ui'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'

interface MobileNavProps {
  data: HeaderType
  locale: string
}

export const MobileNav: React.FC<MobileNavProps> = ({ data, locale }) => {
  const [open, setOpen] = useState(false)
  const pathname = usePathname()
  const navItems = data?.navItems || []

  const t = {
    uk: {
      menu: 'Меню',
      close: 'Закрити',
    },
    en: {
      menu: 'Menu',
      close: 'Close',
    },
  }[locale] || { menu: 'Menu', close: 'Close' }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          aria-label={t.menu}
          aria-expanded={open}
        >
          <Menu size={24} />
        </Button>
      </DrawerTrigger>

      <DrawerContent>
        <VisuallyHidden>
          <DrawerTitle>{t.menu}</DrawerTitle>
        </VisuallyHidden>

        <nav className="flex flex-col py-4" aria-label="Mobile navigation">
          {navItems.map(({ link }, i) => {
            const isActive =
              pathname === link.url || (link.url !== '/' && pathname?.startsWith(link.url || ''))

            return (
              <button
                key={i}
                onClick={() => setOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-6 py-4 text-base font-medium transition-colors',
                  'active:bg-accent/80',
                  isActive && 'bg-primary/10 text-primary ',
                )}
              >
                <CMSLink
                  {...link}
                  appearance="link"
                  className={cn(
                    'flex-1 text-left no-underline hover:no-underline',
                    isActive ? 'text-primary' : 'text-foreground',
                  )}
                />
              </button>
            )
          })}
        </nav>

        <div className="px-6 py-4 border-t border-border">
          <div className="flex items-center justify-center">
            <LanguageSwitcher currentLocale={locale as Locale} variant="mobile" />
          </div>
        </div>

        <DrawerFooter className="pt-4 pb-8">
          <DrawerClose asChild>
            <Button variant="outline" className="w-full">
              <X className="size-4 mr-2" />
              {t.close}
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
